import { useEffect, useRef, useState } from 'react'
import { Shield } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FIELD_APP_HOME_PATH, isFieldAppAllowedPath, isFieldAppHost } from '../lib/fieldAppHost'

const SME_STAGING_HOST = 'sme.staging.ascsurvey.com'
const SME_SEED_LOGIN_HASH_PREFIX = '#sme_seed_login='

/**
 * Hosts allowed to honor the seed-login fragment when the frontend is NOT
 * running in production. Lets the local-device HITL runbook
 * (`docs/runbooks/local-device-hitl-testing.md`) ship a one-tap QR for
 * seeded accounts without handing plaintext credentials to the tester.
 * Production must never allow these.
 */
const DEV_SEED_LOGIN_HOST_PATTERNS: ReadonlyArray<RegExp> = [
  /^localhost$/,
  /^127\.0\.0\.1$/,
  /^192\.168\.[0-9]+\.[0-9]+$/,
  /^10\.[0-9]+\.[0-9]+\.[0-9]+$/,
  /^172\.(1[6-9]|2[0-9]|3[0-1])\.[0-9]+\.[0-9]+$/,
  /\.trycloudflare\.com$/,
  /\.ngrok\.io$/,
  /\.ngrok-free\.app$/,
]

function isDevSeedLoginHost(hostname: string): boolean {
  const env = (import.meta.env.VITE_ENVIRONMENT as string | undefined)?.toLowerCase()
  // Never accept dev hosts on production builds — only staging or local.
  if (env === 'production') return false
  return DEV_SEED_LOGIN_HOST_PATTERNS.some((re) => re.test(hostname))
}

export type SmeSeedLoginPayload = {
  email: string
  password: string
  role?: string
  tier?: string
}

export function landingPathForRole(role: string | undefined, fieldAppHost = isFieldAppHost()): string {
  const r = (role || '').toLowerCase()
  if (r === 'field_surveyor') return '/today'
  if (fieldAppHost) return FIELD_APP_HOME_PATH
  if (r === 'reviewer') return '/reviewer'
  return '/dashboard'
}

export function resolveSafeFromPath(value: unknown, role?: string, fieldAppHost = isFieldAppHost()): string {
  if (typeof value !== 'string') return landingPathForRole(role, fieldAppHost)
  if (!value.startsWith('/')) return landingPathForRole(role, fieldAppHost)
  if (value.startsWith('//')) return landingPathForRole(role, fieldAppHost)
  if (fieldAppHost && !isFieldAppAllowedPath(value)) return landingPathForRole(role, fieldAppHost)
  return value
}

export function resolvePostLoginPath(
  role: string | undefined,
  fromPath: unknown,
  fieldAppHost = isFieldAppHost(),
): string {
  const normalizedRole = (role || '').toLowerCase()
  if (normalizedRole === 'field_surveyor') {
    return landingPathForRole(role, fieldAppHost)
  }
  return resolveSafeFromPath(fromPath, role, fieldAppHost)
}

function resolveCheckoutHandoffPath(search: string, role: string | undefined): string | null {
  const roleValue = (role || '').toLowerCase()
  if (roleValue !== 'admin' && roleValue !== 'superadmin') return null
  const params = new URLSearchParams(search)
  const tier = params.get('checkout_tier')
  if (!tier) return null
  const interval = params.get('billing_interval') || 'monthly'
  return `/billing?checkout_tier=${encodeURIComponent(tier)}&billing_interval=${encodeURIComponent(interval)}`
}

function resolveSignupPath(search: string): string | null {
  const params = new URLSearchParams(search)
  const tier = params.get('checkout_tier')
  if (!tier) return null
  const interval = params.get('billing_interval') || 'monthly'
  return `/signup?checkout_tier=${encodeURIComponent(tier)}&billing_interval=${encodeURIComponent(interval)}`
}

function decodeBase64UrlJson(value: string): unknown {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`
  const json = atob(padded)
  return JSON.parse(json)
}

export function resolveSmeSeedLoginPayload(
  hash: string,
  hostname: string,
): SmeSeedLoginPayload | null {
  if (hostname !== SME_STAGING_HOST && !isDevSeedLoginHost(hostname)) return null
  if (!hash.startsWith(SME_SEED_LOGIN_HASH_PREFIX)) return null

  try {
    const data = decodeBase64UrlJson(hash.slice(SME_SEED_LOGIN_HASH_PREFIX.length))
    if (!data || typeof data !== 'object') return null
    const payload = data as Record<string, unknown>
    if (typeof payload.email !== 'string' || payload.email.trim().length === 0) return null
    if (typeof payload.password !== 'string' || payload.password.length === 0) return null
    return {
      email: payload.email,
      password: payload.password,
      role: typeof payload.role === 'string' ? payload.role : undefined,
      tier: typeof payload.tier === 'string' ? payload.tier : undefined,
    }
  } catch {
    return null
  }
}

export function LoginPage() {
  const { session, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seedLoginAttemptedRef = useRef(false)
  const signupPath = resolveSignupPath(location.search)

  useEffect(() => {
    if (session) {
      const checkoutHandoff = resolveCheckoutHandoffPath(location.search, session.user.app_metadata?.role)
      if (checkoutHandoff) {
        navigate(checkoutHandoff, { replace: true })
        return
      }
      const fromPath = resolvePostLoginPath(
        session.user.app_metadata?.role,
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname,
      )
      navigate(fromPath, { replace: true })
    }
  }, [session, location.search, location.state, navigate])

  useEffect(() => {
    if (session || seedLoginAttemptedRef.current || typeof window === 'undefined') return

    const payload = resolveSmeSeedLoginPayload(window.location.hash, window.location.hostname)
    if (!payload) return

    seedLoginAttemptedRef.current = true
    window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`)
    Promise.resolve().then(() => {
      setEmail(payload.email)
      setPassword(payload.password)
      setIsLoading(true)
      setError(null)
      return signIn(payload.email, payload.password)
    }).then(({ error, hasSession }) => {
      if (error) {
        setError(error.message)
        setIsLoading(false)
        return
      }

      if (hasSession) {
        setIsLoading(false)
        return
      }

      setError('SME one-tap sign-in did not establish an active session.')
      setIsLoading(false)
    })
  }, [session, signIn])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    const { error, hasSession } = await signIn(email, password)
    if (error) {
      setError(error.message)
      setIsLoading(false)
      return
    }

    if (hasSession) {
      // signIn set the session in AuthContext; the useEffect above will
      // fire on session change and navigate with role-based landing.
      setIsLoading(false)
      return
    }

    setError('Sign-in succeeded but no active session was established. Verify frontend env and Supabase auth settings.')
    setIsLoading(false)
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f6fbff,_#e9eff6_52%,_#e3ebf3)] px-4 py-8">
      <div className="mx-auto max-w-md">

        <div className="app-surface overflow-hidden">
          <div className="border-b border-slate-200 bg-white px-6 py-4">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-[var(--brand-primary)] p-1.5 text-white">
                <Shield size={16} />
              </div>
              <span className="font-brand text-xl font-bold text-[var(--brand-deep)]">ASC Survey</span>
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-600">ASC Site Visit Platform</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 px-6 py-5">
            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                Email
              </label>
              <input
                id="email"
                name="email"
                autoComplete="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                Password
              </label>
              <input
                id="password"
                name="password"
                autoComplete="current-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={isLoading} className="btn-primary w-full py-2.5 text-sm disabled:opacity-60">
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <Link to="/forgot-password" className="block w-full text-center text-sm font-semibold text-[var(--brand-primary)] hover:underline">
              Forgot Password?
            </Link>
            {signupPath && (
              <Link to={signupPath} className="block w-full text-center text-sm font-semibold text-[var(--brand-primary)] hover:underline">
                New here? Create your trial account
              </Link>
            )}
          </form>

          <div className="bg-[var(--brand-primary)] px-6 py-2 text-center text-xs font-semibold tracking-wide text-blue-50">
            Compliance. Accuracy. Security.
          </div>
        </div>
      </div>
    </div>
  )
}
